/**
 * Notification chime generated with Web Audio (no audio files, no CDN). Browsers only allow audio
 * after a user gesture, so the AudioContext is created/resumed on the first pointer or key press.
 */

type AudioCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let lastPlayedAt = 0;
const MIN_GAP_MS = 1_500;

function audioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext ?? null;
}

export function isAudioSupported(): boolean {
  return audioCtor() !== null;
}

/** Create or resume the context. Only call from a user gesture (or when it already exists). */
export function unlockAudio(): AudioContext | null {
  const Ctor = audioCtor();
  if (!Ctor) return null;
  try {
    ctx ??= new Ctor();
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  } catch {
    return null;
  }
  return ctx;
}

/** Listens for the first gestures and unlocks audio; returns a cleanup function. */
export function installAudioUnlock(): () => void {
  const events = ['pointerdown', 'keydown', 'touchend'] as const;
  const onGesture = () => {
    const c = unlockAudio();
    if (c && c.state === 'running') remove();
  };
  const remove = () => {
    for (const e of events) window.removeEventListener(e, onGesture, true);
  };
  for (const e of events) window.addEventListener(e, onGesture, { capture: true, passive: true });
  return remove;
}

/**
 * Two short rising sine notes. `volume` 0–100 (perceptual curve). Returns false when audio is
 * still locked/unsupported or a chime played within the last 1.5 s (bursts make one sound).
 */
export function playChime(volume: number, { force = false }: { force?: boolean } = {}): boolean {
  if (!ctx) return false;
  const now = Date.now();
  if (!force && now - lastPlayedAt < MIN_GAP_MS) return false;
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  if (ctx.state !== 'running' && !force) return false;
  const level = Math.max(0, Math.min(100, volume)) / 100;
  if (level <= 0) return false;
  lastPlayedAt = now;
  const peak = 0.35 * level * level + 0.02;
  const start = ctx.currentTime + 0.01;
  const notes: Array<[freq: number, at: number]> = [
    [880, 0],
    [1318.5, 0.13],
  ];
  for (const [freq, offset] of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const t0 = start + offset;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.25);
  }
  return true;
}
